import React, { useEffect, useRef, useState } from 'react'
import Formsy from 'formsy-react'
import { fetchNuveiSessionToken } from 'credit-cards/nuvei/api'
import FormsyInput from '@components/Textfield/withFormsy'
import Button from 'shared/components/Button'
import CircleLoader from 'shared/components/CircleLoader'
import ErrorMessage from 'shared/components/ErrorMessage'
import { languageFromPath } from 'shared/country'

// Import styles globally (no styles object needed)
import './styles.scss'

const merchantId = window.PAYMENT_SERVICE_MERCHANT_ID
const merchantSiteId = window.PAYMENT_SERVICE_MERCHANT_SITE_ID
const nuveiEnv = window.PAYMENT_SERVICE_ENV
const SUBMIT_SUCCESS = 'SUCCESS'
// Force input fields to 16px
const style = {
    base: {
        fontSize: '16px',
    },
}

const AddCreditCardForm = ({ onTokenReceived, translate: t, isTokenizationPage }) => {
    const [error, setError] = useState(null)
    const [ccTokenLoading, setCCTokenLoading] = useState(false)
    const [sessionLoading, setSessionLoading] = useState(true)
    const [ccTempToken, setCcTempToken] = useState(null)
    const [sessionToken, setSessionToken] = useState(null)
    const [cardHolder, setCardHolder] = useState('')
    const [cardN, setCardN] = useState(null)
    const [safeCharge, setSafeCharge] = useState(null)

    // State to track the validity of each field since we are using external
    // library for credit card fields, we need to manage the validity ourselves
    // in addition to our own Formsy fields.
    const [fieldsValid, setFieldsValid] = useState({
        ccNumber: false,
        ccExpiration: false,
        ccCvc: false,
        cardHolderName: false,
        formValid: false,
    })

    useEffect(() => {
        fetchSessionToken()
    }, [])

    useEffect(() => {
        if (!window.SafeCharge || !sessionToken) return

        const newSafeCharge = window.SafeCharge({
            env: nuveiEnv,
            merchantId,
            merchantSiteId,
            sessionToken,
        })

        if (newSafeCharge) {
            const safeChargeFields = newSafeCharge.fields({
                locale: languageFromPath(),
            })

            const cardNumber = safeChargeFields.create('ccNumber', { style })
            cardNumber.attach('#card-number')
            cardNumber.on('change', evt => setValidity('ccNumber', evt.complete))

            const cardExpiry = safeChargeFields.create('ccExpiration', { style })
            cardExpiry.attach('#card-expiry')
            cardExpiry.on('change', evt => setValidity('ccExpiration', evt.complete))

            const cardCvc = safeChargeFields.create('ccCvc', { style })
            cardCvc.attach('#card-cvc')
            cardCvc.on('change', evt => setValidity('ccCvc', evt.complete))

            setCardN(cardNumber)
            setSafeCharge(newSafeCharge)
        }
    }, [sessionToken])

    // Fetch sessionToken on mount
    const fetchSessionToken = async () => {
        try {
            setSessionLoading(true)
            setError(null)

            const response = await fetchNuveiSessionToken()

            if (response.Token) {
                setSessionToken(response.Token)
            } else {
                throw new Error(t('nuvei.creditcard.error.sessionToken'))
            }
        } catch (err) {
            setError(err.message)
        } finally {
            setSessionLoading(false)
        }
    }

    const setValidity = (field, isValid) => {
        if (isValid !== fieldsValid[field]) {
            setFieldsValid(prev => ({
                ...prev,
                [field]: isValid,
            }))
        }
    }

    const handleCardHolderChange = e => {
        setCardHolder(e.target.value)
        setValidity('cardHolderName', e.target.value.trim().length > 0)
    }

    const handleSubmit = async () => {
        setCCTokenLoading(true)
        setError(null)
        setCcTempToken(null)

        try {
            const result = await safeCharge.getToken(cardN, {
                sessionToken: sessionToken,
                cardHolderName: cardHolder,
            })

            // Check if all fields are valid
            if (result.status === SUBMIT_SUCCESS) {
                // Combine the tokens or use the card number token as the main token
                setCcTempToken(result.ccTempToken)
                if (onTokenReceived) {
                    onTokenReceived({
                        CustomerName: cardHolder,
                        CardNumber: result.ccTempToken,
                        SessionToken: sessionToken,
                    })
                }
            } else {
                setError(result.reason || t('nuvei.creditcard.error.cctoken'))
            }
        } catch (err) {
            setError(err.message)
        }

        setCCTokenLoading(false)
    }

    // Show loading state while fetching sessionToken
    if (sessionLoading) {
        return <CircleLoader />
    }

    // If there is not sessionToken, then there is an error with sessionToken
    // and form is still hidden to show error under the form.
    if (!sessionToken && error) {
        return <ErrorMessage type="text" messages={[error]} />
    }

    if (isTokenizationPage && ccTempToken) {
        return (
            <div className="d-flex justify-content-center mt-5">
                <p className="hidden" data-nuvei-cardholder={cardHolder} className="invisible" />
                <p className="hidden" data-nuvei-token={ccTempToken} className="invisible" />
                <p className="hidden" data-nuvei-session-token={sessionToken} className="invisible" />
                <CircleLoader />
            </div>
        )
    }

    return (
        <div className="nuveiContainer">
            <Formsy
                onSubmit={handleSubmit}
                onValid={() => setValidity('formValid', true)}
                onInvalid={() => setValidity('formValid', false)}>
                <div className="mt-3">
                    <label htmlFor="card-holder-name">
                        <T>nuvei.creditcard.label.cardHolder</T>
                    </label>
                    <FormsyInput
                        type="text"
                        name="card-holder-name"
                        placeholder={t('nuvei.creditcard.placeholder.cardHolder')}
                        onChange={handleCardHolderChange}
                        required
                        validationError={t('input.required.error.message')}
                    />
                </div>
                <div className="mt-3">
                    <label htmlFor="card-number">
                        <T>nuvei.creditcard.label.number</T>
                    </label>
                    <div id="card-number" className="card-field" />
                </div>
                <div className="row mt-3 align-items-end">
                    <div className="col-6">
                        <label htmlFor="card-expiry">
                            <T>nuvei.creditcard.label.expiry</T>
                        </label>
                        <div id="card-expiry" className="card-field" />
                    </div>
                    <div className="col-6">
                        <label htmlFor="card-cvc">
                            <T>nuvei.creditcard.label.cvc</T>
                        </label>
                        <div id="card-cvc" className="card-field" />
                    </div>
                </div>
                <Button
                    type="submit"
                    disabled={ccTokenLoading || Object.values(fieldsValid).some(f => Boolean(f) === false)}
                    className="mt-5">
                    {ccTokenLoading ? (
                        <CircleLoader size="small" className="w-100" />
                    ) : (
                        <T>nuvei.creditcard.submit.label</T>
                    )}
                </Button>
                {error && !ccTokenLoading && <ErrorMessage type="text" messages={[error]} />}
            </Formsy>
        </div>
    )
}

export default withTranslate(AddCreditCardForm)
